import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getTradingOrders,
  postTradingOrdersOrderIdCancel,
} from "../api/endpoints/trading/trading";
import type { GetTradingOrders200Pagination } from "../api/models/getTradingOrders200Pagination";
import type { Order } from "../api/models/order";
import { ClosePositionModal } from "../components/ClosePositionModal";
import { Toast } from "../components/Toast";
import { getBearerToken } from "../lib/authClient";
import {
  isClosedPosition,
  isOpenPosition,
  isPendingOrder,
} from "../lib/linearOrders";

import "./FuturesPositionsPage.css";

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

type Tab = "positions" | "orders";

type ToastState = {
  message: string;
  type?: "error" | "success" | "info";
};

const POLL_INTERVAL_MS = 7000;

const STATUS_LABELS: Record<string, string> = {
  NEW: "Aberta",
  PARTIALLY_FILLED: "Parcial",
  FILLED: "Executada",
  CANCELLED: "Cancelada",
  REJECTED: "Rejeitada",
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pnlClass = (value: string | null | undefined) =>
  Number(value ?? "0") >= 0 ? "futures-pnl--positive" : "futures-pnl--negative";

const signed = (value: string) =>
  Number(value) > 0 && !value.startsWith("+") ? `+${value}` : value;

/** Proximidade do mark price ao preço de liquidação (fração; null se indisponível) */
const liqProximity = (order: Order): number | null => {
  const mark = Number(order.linear?.markPrice ?? "");
  const liq = Number(order.linear?.liqPrice ?? "");
  if (!Number.isFinite(mark) || !Number.isFinite(liq) || mark <= 0) return null;
  return Math.abs(mark - liq) / mark;
};

export default function FuturesPositionsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [tab, setTab] = useState<Tab>("positions");
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<
    GetTradingOrders200Pagination | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [closeTarget, setCloseTarget] = useState<Order | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Toast vindo da confirmação da ordem (TradePage)
  useEffect(() => {
    const state = location.state as { toast?: ToastState } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Espelho síncrono da lista — permite que fetchOrders retorne o resultado
  // mesclado sem depender do timing do updater do setOrders.
  const ordersRef = useRef<Order[]>([]);

  const fetchOrders = useCallback(
    async (opts?: { silent?: boolean; cursor?: string }): Promise<
      Order[] | null
    > => {
      const { silent = false, cursor } = opts ?? {};
      if (!silent && !cursor) setLoading(true);
      if (cursor) setLoadingMore(true);
      try {
        const response = await getTradingOrders(
          { product: "FUTURE", limit: 20, ...(cursor ? { cursor } : {}) },
          authHeaders(),
        );
        if (response.status === 200 && "data" in response.data) {
          const page = response.data.data;
          setPagination(response.data.pagination ?? null);
          setError(null);
          let next: Order[];
          if (cursor) {
            const seen = new Set(ordersRef.current.map((o) => o.internalId));
            next = [
              ...ordersRef.current,
              ...page.filter((o) => !seen.has(o.internalId)),
            ];
          } else {
            next = page;
          }
          ordersRef.current = next;
          setOrders(next);
          return next;
        }
        const msg = (response.data as { message?: string })?.message ??
          `Falha ao buscar ordens (status ${response.status}).`;
        if (!silent) setError(msg);
        return null;
      } catch (err) {
        console.error("Erro ao buscar ordens de futuros:", err);
        if (!silent) {
          setError(err instanceof Error ? err.message : "Erro desconhecido");
        }
        return null;
      } finally {
        if (!silent && !cursor) setLoading(false);
        if (cursor) setLoadingMore(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Polling enquanto houver posição aberta ou ordem pendente (TP/SL podem
  // disparar a qualquer momento — o backend reconcilia e a lista reflete).
  const hasLive = orders.some((o) => isOpenPosition(o) || isPendingOrder(o));
  useEffect(() => {
    if (!hasLive) return;
    const handle = window.setInterval(() => {
      fetchOrders({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [hasLive, fetchOrders]);

  // Refetch ao focar a janela
  useEffect(() => {
    const onFocus = () => fetchOrders({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchOrders]);

  const cancelGuard = useRef(false);
  const handleCancel = async (order: Order) => {
    if (cancelGuard.current) return;
    if (!window.confirm("Cancelar esta ordem? A margem reservada volta ao saldo.")) {
      return;
    }
    cancelGuard.current = true;
    setCancellingId(order.internalId);
    try {
      // O contrato 1.0.45 expõe o internalId (id canônico) direto na ordem.
      const response = await postTradingOrdersOrderIdCancel(
        order.internalId,
        authHeaders(),
      );
      if (response.status === 200) {
        setToast({
          type: "success",
          message: "Ordem cancelada. A margem reservada voltou ao saldo.",
        });
        fetchOrders({ silent: true });
      } else {
        const body = response.data as {
          message?: string;
          error?: { code?: string };
        };
        if (body?.error?.code === "E_USE_CLOSE_POSITION") {
          // A ordem já virou posição — abrir o modal de fechamento no lugar
          const fresh = await fetchOrders({ silent: true });
          const updated = fresh?.find((o) => o.internalId === order.internalId);
          if (updated && isOpenPosition(updated)) {
            setCloseTarget(updated);
          } else {
            setToast({
              type: "info",
              message: "A ordem já virou posição. Use o fechamento.",
            });
          }
        } else if (response.status === 409 || response.status === 404) {
          setToast({
            type: "info",
            message: body?.message ?? "O estado da ordem mudou. Atualizando...",
          });
          fetchOrders({ silent: true });
        } else if (body?.error?.code === "E_BYBIT_SUBACCOUNT_MISSING") {
          setToast({
            type: "error",
            message: "Conta de trading não configurada. Contate o suporte.",
          });
        } else {
          setToast({
            type: "error",
            message: body?.message ??
              `Falha ao cancelar (status ${response.status}).`,
          });
        }
      }
    } catch (err) {
      console.error("Erro ao cancelar ordem:", err);
      setToast({
        type: "error",
        message: err instanceof Error ? err.message : "Erro desconhecido",
      });
    } finally {
      cancelGuard.current = false;
      setCancellingId(null);
    }
  };

  const openPositions = orders.filter(isOpenPosition);
  const pendingOrders = orders.filter(isPendingOrder);
  const historyOrders = orders.filter(
    (o) => !isOpenPosition(o) && !isPendingOrder(o),
  );

  const renderPositionCard = (order: Order) => {
    const linear = order.linear;
    if (!linear) return null;
    const partiallyClosed = Number(linear.closedQty) > 0;
    const proximity = liqProximity(order);
    const liqAlert = proximity !== null && proximity < 0.05
      ? "futures-liq--danger"
      : proximity !== null && proximity < 0.1
      ? "futures-liq--warn"
      : "";
    const roi = (() => {
      const pnl = Number(linear.unrealisedPnl ?? "");
      const margin = Number(linear.initialMargin ?? "");
      if (!Number.isFinite(pnl) || !Number.isFinite(margin) || margin <= 0) {
        return null;
      }
      return (pnl / margin) * 100;
    })();

    return (
      <div key={order.internalId} className="futures-card">
        <button
          type="button"
          className="futures-cardMain"
          onClick={() => navigate(`/futures/positions/${order.internalId}`)}
        >
          <div className="futures-cardHead">
            <span className="futures-pair">{order.symbol}</span>
            <span
              className={`futures-sideBadge ${
                order.side === "BUY"
                  ? "futures-sideBadge--long"
                  : "futures-sideBadge--short"
              }`}
            >
              {order.side === "BUY" ? "Long" : "Short"}{" "}
              {linear.leverage ? `${linear.leverage}x` : ""}
            </span>
          </div>
          <div className="futures-cardGrid">
            <div className="futures-cell">
              <span className="futures-cellLabel">Tamanho</span>
              <span className="futures-cellValue">
                {partiallyClosed
                  ? `${linear.remainingQty}/${order.qty}`
                  : linear.remainingQty}{" "}
                {order.baseCoin}
              </span>
            </div>
            <div className="futures-cell">
              <span className="futures-cellLabel">P&L não realizado</span>
              <span
                className={`futures-cellValue ${pnlClass(linear.unrealisedPnl)}`}
              >
                {linear.unrealisedPnl != null
                  ? `${signed(linear.unrealisedPnl)} ${order.quoteCoin}`
                  : "—"}
                {roi !== null && (
                  <span className="futures-roi">
                    {" "}({roi >= 0 ? "+" : ""}{roi.toFixed(2)}%)
                  </span>
                )}
              </span>
            </div>
            <div className="futures-cell">
              <span className="futures-cellLabel">Entry</span>
              <span className="futures-cellValue">
                {linear.entryPrice ?? "—"}
              </span>
            </div>
            <div className="futures-cell">
              <span className="futures-cellLabel">Mark</span>
              <span className="futures-cellValue">
                {linear.markPrice ?? "—"}
              </span>
            </div>
            <div className="futures-cell">
              <span className="futures-cellLabel">Liq.</span>
              <span className={`futures-cellValue ${liqAlert}`}>
                {linear.liqPrice ?? "—"}
              </span>
            </div>
            <div className="futures-cell">
              <span className="futures-cellLabel">TP / SL</span>
              <span className="futures-cellValue">
                {linear.takeProfit ?? "—"} / {linear.stopLoss ?? "—"}
              </span>
            </div>
          </div>
        </button>
        <button
          type="button"
          className="futures-closeBtn"
          onClick={() => setCloseTarget(order)}
        >
          Fechar
        </button>
      </div>
    );
  };

  const renderPendingCard = (order: Order) => (
    <div key={order.internalId} className="futures-card">
      <button
        type="button"
        className="futures-cardMain"
        onClick={() => navigate(`/futures/positions/${order.internalId}`)}
      >
        <div className="futures-cardHead">
          <span className="futures-pair">{order.symbol}</span>
          <span
            className={`futures-sideBadge ${
              order.side === "BUY"
                ? "futures-sideBadge--long"
                : "futures-sideBadge--short"
            }`}
          >
            {order.side === "BUY" ? "Long" : "Short"}{" "}
            {order.linear?.leverage ? `${order.linear.leverage}x` : ""}
          </span>
          <span className="futures-statusBadge">
            {STATUS_LABELS[order.orderStatus] ?? order.orderStatus}
          </span>
        </div>
        <div className="futures-cardGrid">
          <div className="futures-cell">
            <span className="futures-cellLabel">Quantidade</span>
            <span className="futures-cellValue">
              {order.qty} {order.baseCoin}
              {order.price ? ` @ ${order.price}` : ""}
            </span>
          </div>
          <div className="futures-cell">
            <span className="futures-cellLabel">Executado</span>
            <span className="futures-cellValue">
              {order.cumExecQty}/{order.qty}
            </span>
          </div>
        </div>
      </button>
      <button
        type="button"
        className="futures-cancelBtn"
        onClick={() => handleCancel(order)}
        disabled={cancellingId === order.internalId}
      >
        {cancellingId === order.internalId ? "Cancelando..." : "Cancelar"}
      </button>
    </div>
  );

  const renderHistoryCard = (order: Order) => {
    const closed = isClosedPosition(order);
    return (
      <button
        key={order.internalId}
        type="button"
        className="futures-card futures-card--history"
        onClick={() => navigate(`/futures/positions/${order.internalId}`)}
      >
        <div className="futures-cardHead">
          <span className="futures-pair">{order.symbol}</span>
          <span
            className={`futures-sideBadge ${
              order.side === "BUY"
                ? "futures-sideBadge--long"
                : "futures-sideBadge--short"
            }`}
          >
            {order.side === "BUY" ? "Long" : "Short"}
          </span>
          <span className="futures-statusBadge">
            {closed
              ? "Fechada"
              : STATUS_LABELS[order.orderStatus] ?? order.orderStatus}
          </span>
        </div>
        <div className="futures-historyRow">
          <span>
            {order.qty} {order.baseCoin} · {formatDateTime(order.createdAt)}
          </span>
          {closed && order.linear?.realizedPnl != null && (
            <span className={pnlClass(order.linear.realizedPnl)}>
              {signed(order.linear.realizedPnl)} {order.quoteCoin}
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="page-centered">
      <div className="futures-wrap">
        <div className="futures-header">
          <button
            type="button"
            className="futures-iconBtn"
            onClick={() => navigate("/profile")}
            aria-label="Voltar"
          >
            ←
          </button>
          <span className="futures-title">Futuros</span>
          <button
            type="button"
            className="futures-newBtn"
            onClick={() => navigate("/trade/BTCUSDT/future")}
          >
            + Nova
          </button>
        </div>

        <div className="futures-tabs">
          <button
            type="button"
            className={`futures-tab ${tab === "positions" ? "futures-tab--active" : ""}`}
            onClick={() => setTab("positions")}
          >
            Posições{openPositions.length > 0 ? ` (${openPositions.length})` : ""}
          </button>
          <button
            type="button"
            className={`futures-tab ${tab === "orders" ? "futures-tab--active" : ""}`}
            onClick={() => setTab("orders")}
          >
            Ordens{pendingOrders.length > 0 ? ` (${pendingOrders.length})` : ""}
          </button>
        </div>

        {loading && <div className="futures-status">Carregando...</div>}

        {error && !loading && (
          <div className="futures-status futures-status--error">{error}</div>
        )}

        {!loading && !error && tab === "positions" && (
          <>
            {openPositions.length === 0 && (
              <div className="futures-status">
                Nenhuma posição aberta.
              </div>
            )}
            {openPositions.map(renderPositionCard)}
          </>
        )}

        {!loading && !error && tab === "orders" && (
          <>
            {pendingOrders.length > 0 && (
              <div className="futures-sectionTitle">Pendentes</div>
            )}
            {pendingOrders.map(renderPendingCard)}

            {historyOrders.length > 0 && (
              <div className="futures-sectionTitle">Histórico recente</div>
            )}
            {historyOrders.map(renderHistoryCard)}

            {pendingOrders.length === 0 && historyOrders.length === 0 && (
              <div className="futures-status">Nenhuma ordem encontrada.</div>
            )}

            {pagination?.hasMoreAfter && pagination.nextCursor && (
              <button
                type="button"
                className="futures-loadMore"
                onClick={() =>
                  fetchOrders({ cursor: pagination.nextCursor ?? undefined })}
                disabled={loadingMore}
              >
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </button>
            )}
          </>
        )}

        {closeTarget && (
          <ClosePositionModal
            order={closeTarget}
            onDismiss={() => setCloseTarget(null)}
            onClosed={(result) => {
              const quoteCoin = closeTarget.quoteCoin;
              setCloseTarget(null);
              setToast({
                type: "success",
                message: result.fullyClosed
                  ? `Posição fechada — P&L: ${result.realizedPnl} ${quoteCoin}`
                  : `Fechamento parcial — P&L: ${result.realizedPnl} ${quoteCoin}`,
              });
              fetchOrders({ silent: true });
            }}
            onStale={(message) => {
              setCloseTarget(null);
              setToast({ type: "info", message });
              fetchOrders({ silent: true });
            }}
          />
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
}
